import type { ScoreResult, ScoringConfig, GradingResult } from './types';

/**
 * Default scoring configuration
 */
export const DEFAULT_SCORING_CONFIG: Required<ScoringConfig> = {
  max: 1,
  partialCreditEnabled: false,
  penaltyPerWrongAnswer: 0,
  passingScore: 0.6,
};

/**
 * ScoringEngine - Calculates scores for content
 */
export class ScoringEngine {
  private config: Required<ScoringConfig>;

  constructor(config: ScoringConfig = {}) {
    this.config = { ...DEFAULT_SCORING_CONFIG, ...config };
  }

  /**
   * Get config
   */
  getConfig(): Required<ScoringConfig> {
    return { ...this.config };
  }

  /**
   * Update config
   */
  setConfig(config: Partial<ScoringConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Calculate score for a single answer
   */
  calculateScore(isCorrect: boolean, attempts: number = 1): ScoreResult {
    const { max: maxPoints, penaltyPerWrongAnswer, passingScore } = this.config;
    let score = isCorrect ? maxPoints : 0;

    // Apply penalty for wrong answers
    if (!isCorrect && penaltyPerWrongAnswer > 0) {
      const penalty = (attempts - 1) * penaltyPerWrongAnswer;
      score = Math.max(0, score - penalty);
    }

    const percentage = (score / maxPoints) * 100;

    return {
      score,
      max: maxPoints,
      percentage,
      isCorrect,
      isPassing: percentage >= passingScore * 100,
      penalty: penaltyPerWrongAnswer ? (attempts - 1) * penaltyPerWrongAnswer : 0,
    };
  }

  /**
   * Calculate partial credit score
   */
  calculatePartialScore(
    correctCount: number,
    totalCount: number,
    wrongCount: number = 0
  ): ScoreResult {
    const { max: maxPoints, partialCreditEnabled, penaltyPerWrongAnswer, passingScore } = this.config;

    if (!partialCreditEnabled) {
      const isCorrect = correctCount === totalCount && wrongCount === 0;
      return {
        score: isCorrect ? maxPoints : 0,
        max: maxPoints,
        percentage: isCorrect ? 100 : 0,
        isCorrect,
        isPassing: isCorrect ? true : false,
      };
    }

    // Partial credit calculation
    const correctRatio = totalCount > 0 ? correctCount / totalCount : 0;
    const penalty = wrongCount * penaltyPerWrongAnswer;
    const score = Math.max(0, maxPoints * correctRatio - penalty);
    const percentage = (score / maxPoints) * 100;

    return {
      score,
      max: maxPoints,
      percentage,
      isCorrect: correctCount === totalCount && wrongCount === 0,
      isPassing: percentage >= passingScore * 100,
      partialCredit: correctRatio,
      penalty,
    };
  }

  /**
   * Grade single/multiple choice answer
   */
  gradeChoice(
    selectedIds: string | string[],
    correctIds: string | string[]
  ): GradingResult {
    const selected = Array.isArray(selectedIds) ? selectedIds : [selectedIds];
    const correct = Array.isArray(correctIds) ? correctIds : [correctIds];

    // Single choice
    if (correct.length === 1) {
      const isCorrect = selected.length === 1 && selected[0] === correct[0];
      return {
        isCorrect,
        score: isCorrect ? this.config.max : 0,
        max: this.config.max,
        correctAnswer: correct[0],
      };
    }

    // Multiple choice
    const correctSelected = selected.filter((id) => correct.includes(id)).length;
    const wrongSelected = selected.filter((id) => !correct.includes(id)).length;

    const scoreResult = this.calculatePartialScore(
      correctSelected,
      correct.length,
      wrongSelected
    );

    return {
      isCorrect: scoreResult.isCorrect,
      score: scoreResult.score,
      max: scoreResult.max,
      correctAnswer: correct,
      details: {
        correctSelected,
        wrongSelected,
        totalCorrect: correct.length,
      },
    };
  }

  /**
   * Grade text answer (exact match or normalized)
   */
  gradeText(
    answer: string,
    correctAnswer: string | string[],
    options: { caseSensitive?: boolean; trimWhitespace?: boolean } = {}
  ): GradingResult {
    const { caseSensitive = false, trimWhitespace = true } = options;
    const correctAnswers = Array.isArray(correctAnswer) ? correctAnswer : [correctAnswer];

    const normalize = (s: string) => {
      let result = s;
      if (trimWhitespace) result = result.trim();
      if (!caseSensitive) result = result.toLowerCase();
      return result;
    };

    const normalizedAnswer = normalize(answer);
    const isCorrect = correctAnswers.some((c) => normalize(c) === normalizedAnswer);

    return {
      isCorrect,
      score: isCorrect ? this.config.max : 0,
      max: this.config.max,
      correctAnswer: correctAnswers[0],
    };
  }

  /**
   * Grade numeric answer with tolerance
   */
  gradeNumber(
    answer: number,
    correctAnswer: number,
    tolerance: number = 0
  ): GradingResult {
    const diff = Math.abs(answer - correctAnswer);
    const isCorrect = diff <= tolerance;

    return {
      isCorrect,
      score: isCorrect ? this.config.max : 0,
      max: this.config.max,
      correctAnswer,
      details: { difference: diff, tolerance },
    };
  }

  /**
   * Grade ordering/sequence answer
   */
  gradeOrder(answer: string[], correctOrder: string[]): GradingResult {
    if (answer.length !== correctOrder.length) {
      return {
        isCorrect: false,
        score: 0,
        max: this.config.max,
        correctAnswer: correctOrder,
      };
    }

    let correctPositions = 0;
    for (let i = 0; i < answer.length; i++) {
      if (answer[i] === correctOrder[i]) {
        correctPositions++;
      }
    }

    const scoreResult = this.calculatePartialScore(correctPositions, correctOrder.length);

    return {
      isCorrect: scoreResult.isCorrect,
      score: scoreResult.score,
      max: scoreResult.max,
      correctAnswer: correctOrder,
      details: { correctPositions, totalPositions: correctOrder.length },
    };
  }

  /**
   * Grade matching/pairing answer
   */
  gradeMatching(
    answer: Record<string, string>,
    correctPairs: Record<string, string>
  ): GradingResult {
    const correctKeys = Object.keys(correctPairs);
    let correctCount = 0;
    const itemResults: Record<string, boolean> = {};

    for (const key of correctKeys) {
      const isCorrect = answer[key] === correctPairs[key];
      itemResults[key] = isCorrect;
      if (isCorrect) correctCount++;
    }

    const scoreResult = this.calculatePartialScore(correctCount, correctKeys.length);

    return {
      isCorrect: scoreResult.isCorrect,
      score: scoreResult.score,
      max: scoreResult.max,
      correctAnswer: correctPairs,
      details: { correctCount, totalPairs: correctKeys.length, itemResults },
    };
  }

  /**
   * Check if score passes
   */
  isPassing(score: number, maxOverride?: number): boolean {
    const maxPoints = maxOverride ?? this.config.max;
    const percentage = (score / maxPoints) * 100;
    return percentage >= this.config.passingScore * 100;
  }

  /**
   * Aggregate scores from multiple content items
   */
  aggregateScores(results: ScoreResult[]): ScoreResult {
    const totalScore = results.reduce((sum, r) => sum + r.score, 0);
    const totalMax = results.reduce((sum, r) => sum + r.max, 0);
    const percentage = totalMax > 0 ? (totalScore / totalMax) * 100 : 0;

    return {
      score: totalScore,
      max: totalMax,
      percentage,
      isCorrect: results.every((r) => r.isCorrect),
      isPassing: percentage >= this.config.passingScore * 100,
    };
  }
}

/**
 * Create a scoring engine
 */
export function createScoringEngine(config?: ScoringConfig): ScoringEngine {
  return new ScoringEngine(config);
}

/**
 * Quick score calculation (stateless)
 */
export function calculateScore(
  isCorrect: boolean,
  config?: ScoringConfig
): ScoreResult {
  const engine = new ScoringEngine(config);
  return engine.calculateScore(isCorrect);
}

/**
 * Quick grade choice (stateless)
 */
export function gradeChoice(
  selectedIds: string | string[],
  correctIds: string | string[],
  config?: ScoringConfig
): GradingResult {
  const engine = new ScoringEngine(config);
  return engine.gradeChoice(selectedIds, correctIds);
}
